import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api from "@/lib/appApi";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2, Crown } from "lucide-react";

export default function PaymentResult() {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();
  const isSuccess = location.pathname.includes("success");
  const [state, setState] = useState(isSuccess ? "checking" : "cancelled");
  const attempts = useRef(0);

  useEffect(() => {
    if (!isSuccess) return;
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          await checkAuth();
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(res.data.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) {}
      if (attempts.current < 12) {
        setTimeout(poll, 2000);
      } else {
        setState("timeout");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [isSuccess, location.search, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground p-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
            <h2 className="font-heading text-2xl font-extrabold mt-4">Confirming payment…</h2>
            <p className="text-muted-foreground text-sm mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full brand-gradient-bg flex items-center justify-center mx-auto">
              <Crown className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-heading text-2xl font-extrabold mt-4" data-testid="payment-success">Premium unlocked!</h2>
            <p className="text-muted-foreground text-sm mt-2">You can now set your gender and filter your matches.</p>
            <Button onClick={() => navigate("/app")} data-testid="go-to-app-btn" className="brand-gradient-bg text-white border-0 rounded-full mt-6 w-full h-11">Start chatting</Button>
          </>
        )}
        {(state === "cancelled" || state === "error" || state === "timeout") && (
          <>
            <XCircle className="w-14 h-14 text-muted-foreground mx-auto" />
            <h2 className="font-heading text-2xl font-extrabold mt-4" data-testid="payment-cancel">
              {state === "cancelled" ? "Checkout cancelled" : state === "timeout" ? "Still processing" : "Something went wrong"}
            </h2>
            <p className="text-muted-foreground text-sm mt-2">
              {state === "timeout" ? "Your payment is taking longer than expected. It will activate automatically once confirmed." : "No charge was made. You can try again anytime."}
            </p>
            <div className="flex gap-2 mt-6">
              <Button variant="outline" onClick={() => navigate("/app")} className="rounded-full flex-1 h-11 border-border bg-secondary">Back to app</Button>
              <Button onClick={() => navigate("/pricing")} className="brand-gradient-bg text-white border-0 rounded-full flex-1 h-11">View plans</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
